import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

/** The red toast a refused mutation shows: its title, and the message used
 * when the rejection is not an `Error` (an `Error`'s own message wins). */
export interface ReviewMutationFailure {
  title: string;
  fallback: string;
}

export interface ReviewQueue<TList> {
  data: TList | null;
  isLoading: boolean;
  isMutating: boolean;
  error: Error | null;
  /** Reload the queue; a missing report id is a no-op. */
  refetch: () => Promise<void>;
  /** Replace the queue in place (a decided row, an added row). */
  setData: Dispatch<SetStateAction<TList | null>>;
  /**
   * Run one mutation against the report: `isMutating` is raised for its
   * duration, `onSuccess` is awaited with the result before it is returned,
   * and a failure (of either step) is toasted as `failure` and rethrown, so
   * a caller can tell a refused mutation from a completed one. Resolves
   * `null` without running when there is no report id. `run` should return
   * the API call's promise as is: a toast shown one promise hop later than
   * the rejection is not seen by a test that awaits the rejection under
   * `act`.
   */
  mutate: <T>(
    failure: ReviewMutationFailure,
    run: (reportId: string) => Promise<T>,
    onSuccess?: (result: T) => void | Promise<void>
  ) => Promise<T | null>;
}

function asError(err: unknown, fallback: string): Error {
  return err instanceof Error ? err : new Error(fallback);
}

/**
 * The plain-state mechanics shared by the review queues (match review,
 * profile review): one list per report, loaded on mount (`null` reportId
 * disables the fetch; a load failure is reported to Sentry and exposed as
 * `error`), and mutations that toast their failure and rethrow. The domain
 * hooks add the row replacement and the recounts on top. `load` must be
 * referentially stable (a module-level function): `refetch` is rebuilt when
 * it changes, and the mount effect runs again.
 */
export function useReviewQueue<TList>(
  reportId: string | null | undefined,
  load: (reportId: string) => Promise<TList>,
  loadFallback: string
): ReviewQueue<TList> {
  const [data, setData] = useState<TList | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (!reportId) {
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setData(await load(reportId));
    } catch (err) {
      const e = asError(err, loadFallback);
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [reportId, load, loadFallback]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const mutate = useCallback(
    async <T>(
      failure: ReviewMutationFailure,
      run: (id: string) => Promise<T>,
      onSuccess?: (result: T) => void | Promise<void>
    ) => {
      if (!reportId) {
        return null;
      }
      try {
        setIsMutating(true);
        const result = await run(reportId);
        await onSuccess?.(result);
        return result;
      } catch (err) {
        const e = asError(err, failure.fallback);
        notifications.show({ title: failure.title, message: e.message, color: 'red' });
        throw e;
      } finally {
        setIsMutating(false);
      }
    },
    [reportId]
  );

  return { data, isLoading, isMutating, error, refetch, setData, mutate };
}
